import { AsyncListLoadFunction, AsyncListLoadOptions } from "@react-stately/data";
import Cookies from "js-cookie";
import { get } from "lodash";
import { Statistic } from "./types";

export const fetcher = async ({
                                  sortDescriptor,
                                  filterText,
                                  cursor,
                                  signal,
                                  url,
                                  statsOnly,
                                  token:_token,
                                  body,
                                  method
                              }: Partial<AsyncListLoadOptions<any, any> & {
    url: string,
    body: object | null,
    method?: "POST" | "GET"
    statsOnly: boolean,
    token?: string
}>) => {
    const fetchUrl = (() => {
        if (cursor) {
            return new URL(cursor);
        }

        if (/^https?:\/\//.test(url)) {
            return new URL(url);
        }

        if (url.startsWith('/ai/') || url.startsWith('ai/')) {
            const aiBase =
                process.env.NEXT_PUBLIC_BACKEND_HOST ||
                (typeof window !== 'undefined' ? window.location.origin : '');

            return new URL(url, aiBase);
        }

        return new URL(url, process.env.NEXT_PUBLIC_API_HOST);
    })();
    const params = fetchUrl.searchParams;
    params.set("filter", filterText || "");

    if (statsOnly) {
        params.set("statsOnly", "true");
    }

    if (sortDescriptor) {
        params.set("sort", JSON.stringify(sortDescriptor));
    }

    if (body && method === "GET") {
        Object.entries(body).forEach(([key, value]) => {
            params.append(key, typeof value === "object" ? JSON.stringify(value) : value);
        });
    }

    let token = _token || Cookies.get("token");

    return fetch(fetchUrl.href, {
        signal,
        method: body && method !== "GET" ? "POST" : "GET",
        body: body && method !== "GET" ? JSON.stringify(body) : null,
        headers: {
            Authorization: "Bearer " + token,
            ["X-Requested-With"]: "XMLHttpRequest",
            "Content-Type": "application/json"
        }
    });
};

export const load =
    <T = any>(url: string, body: object | null = null, method?: "POST" | "GET", setStatistics?: (stats: Partial<Statistic>) => void, token?: string): AsyncListLoadFunction<T, string> =>
        async ({ sortDescriptor, ...rest }) => {
            // Fetch the data
            // React Stately compares request controllers and discards obsolete
            // responses itself. Passing its signal to fetch made abort() throw
            // synchronously from the library's reducer during rapid filtering and
            // Fast Refresh, before this handler could catch it.
            const res = await fetcher({ ...rest, signal: undefined, url, body, method, sortDescriptor, token });
            if (res.status !== 200) {
                return {
                    items: [],
                    cursor: undefined,
                    sortDescriptor
                };
            }

            const json = await res.json();
            const totalItems = Number(json.total ?? json.meta?.total ?? 0);
            const currentPage = Number(json.current_page ?? json.meta?.current_page ?? json.page ?? json.meta?.page ?? 1);
            const perPage = Number(json.per_page ?? json.meta?.per_page ?? json.data?.length ?? 0);
            const hasMore = perPage > 0 && currentPage * perPage < totalItems;
            const nextCursor = (() => {
                if (json.next_page_url) {
                    return json.next_page_url;
                }

                if (!hasMore) {
                    return undefined;
                }

                const nextUrl = new URL(res.url);
                nextUrl.searchParams.set("page", String(currentPage + 1));
                return nextUrl.toString();
            })();

            // Set the new statistic values.
            //
            // Zero is a total like any other — it is the answer a filter that
            // matches nothing gives. Guarding on truthiness left the previous
            // filter's count standing over an empty list, which is the one
            // moment the number is read most closely.
            if (setStatistics) setStatistics({
                totalItems
            });

            return {
                items: json.data ?? [],
                cursor: nextCursor,
                sortDescriptor
            };
        };

export const getCellValue = (columns) => (item, key) =>
    columns.find((col) => col.uid === key)?.renderer?.(item?.[key], item) || get(item, key);
